import type { HealthReportData } from "./index.js";

type BuildHealthHintsOptions = {
  report: HealthReportData;
  connectApiKey?: string;
};

function addHint(target: string[], seen: Set<string>, hint: string): void {
  if (!seen.has(hint)) {
    seen.add(hint);
    target.push(hint);
  }
}

export function buildHealthHints(options: BuildHealthHintsOptions): string[] {
  const { report, connectApiKey } = options;
  const hints: string[] = [];
  const seen = new Set<string>();
  const validNestedRoots = report.nestedSearch.candidates
    .filter((candidate) => candidate.configStatus === "valid")
    .map((candidate) => candidate.projectRoot);

  if (
    !report.runtimeContext.inputProjectRoot &&
    report.projectResolution.configStatus === "unresolved" &&
    report.projectResolution.errorSource === "search"
  ) {
    addHint(
      hints,
      seen,
      "Config was not found from the current working directory. Rerun `--health /absolute/project/path`, pass the same absolute path via MCP tool `projectRoot`, or set `PRIMEUI_PROJECT_ROOT` if your MCP client starts outside the project.",
    );
  }

  if (
    report.runtimeContext.inputProjectRoot &&
    report.projectResolution.primeUiDirStatus === "missing"
  ) {
    if (report.nestedSearch.candidates.length > 0) {
      addHint(
        hints,
        seen,
        validNestedRoots.length > 0
          ? `No direct \`.primeui\` directory was found under the provided path. Rerun health with one of the exact nested project roots that passed config validation, for example: ${validNestedRoots.join(", ")}.`
          : "No direct `.primeui` directory was found under the provided path. Nested `.primeui` directories were found below it, so rerun health with the exact intended project root before using MCP tools.",
      );

      if (validNestedRoots.length > 0) {
        addHint(
          hints,
          seen,
          `When calling MCP tools, pass \`projectRoot\` only for a candidate whose nested health check passes, for example: ${validNestedRoots[0]}.`,
        );
      }
    } else {
      const connectCommand = `npx @primeuicom/cli ${
        connectApiKey ?? "<api-key>"
      } connect`;
      addHint(
        hints,
        seen,
        `No \`.primeui\` directory was found under the provided path. From the intended target folder, run \`${connectCommand}\` to connect the project to PrimeUI.`,
      );
      if (!connectApiKey) {
        addHint(
          hints,
          seen,
          "If you do not already have a valid PrimeUI API key, get it from the appropriate PrimeUI project on `primeui.com`.",
        );
      }
    }
  }

  if (
    report.projectResolution.primeUiDirStatus === "found" &&
    report.projectResolution.configStatus === "missing"
  ) {
    addHint(
      hints,
      seen,
      "`.primeui/project.json` is missing. It was likely corrupted or deleted; restore it by reconnecting the project or by making a fresh export via the PrimeUI CLI tool.",
    );
  }

  if (
    report.projectResolution.primeUiDirStatus === "found" &&
    (report.projectResolution.configStatus === "invalid-json" ||
      report.projectResolution.configStatus === "invalid-format" ||
      report.projectResolution.configStatus === "read-failed")
  ) {
    addHint(
      hints,
      seen,
      "`.primeui/project.json` looks corrupted or incomplete. Restore it by reconnecting the project or by making a fresh export via the PrimeUI CLI tool.",
    );
  }

  if (
    report.runtimeContext.envProjectRoot &&
    report.sourceResolution.projectRoot.comparison === "different"
  ) {
    addHint(
      hints,
      seen,
      report.projectResolution.inputProjectRootValidated
        ? `\`PRIMEUI_PROJECT_ROOT\` points to a different location than the validated explicit input path. Fix or unset \`PRIMEUI_PROJECT_ROOT\`, and use the successful path via MCP tool \`projectRoot\`: ${report.runtimeContext.inputProjectRoot}.`
        : "`PRIMEUI_PROJECT_ROOT` points to a different location than the explicit input path. Correct the project root first, then rerun health before using MCP tool `projectRoot`.",
    );
  }

  if (
    report.sourceResolution.apiKey.effectiveSource === "missing" &&
    !(
      report.projectResolution.primeUiDirStatus === "found" &&
      report.projectResolution.configStatus !== "valid"
    )
  ) {
    addHint(
      hints,
      seen,
      "No usable API key was found. Set `PRIMEUI_API_KEY`, restore `apiKey` in `.primeui/project.json`, or get a valid key from the appropriate PrimeUI project on `primeui.com`.",
    );
  }

  if (
    report.apiCheck.status === "skipped" &&
    report.apiCheck.errorKind === "configuration" &&
    report.runtimeContext.envApiBaseUrl
  ) {
    addHint(
      hints,
      seen,
      "Set `PRIMEUI_API_BASE_URL` to a valid absolute URL such as `http://localhost:3020`, or remove it to fall back to the standard production API.",
    );
  }

  if (
    report.apiCheck.status === "failed" &&
    report.apiCheck.errorKind === "auth"
  ) {
    if (
      report.sourceResolution.apiKey.envState === "set" &&
      report.sourceResolution.apiKey.configState === "set"
    ) {
      addHint(
        hints,
        seen,
        "Both env and config API keys are present. Verify that `PRIMEUI_API_KEY` is not unintentionally overriding a valid config key.",
      );
    }
    if (report.apiCheck.nonStandardBaseUrl) {
      addHint(
        hints,
        seen,
        "Authentication failed while using a non-standard `PRIMEUI_API_BASE_URL`. If you did not intend to target a custom API host, remove that env var and retry.",
      );
    }
    addHint(
      hints,
      seen,
      "The current API key was rejected by `project info`. Refresh the project API key from PrimeUI project settings, then rerun health.",
    );
  }

  if (
    report.apiCheck.status === "failed" &&
    report.apiCheck.errorKind === "network"
  ) {
    addHint(
      hints,
      seen,
      report.apiCheck.nonStandardBaseUrl
        ? "The `project info` request failed while using a non-standard `PRIMEUI_API_BASE_URL`. Verify that host is reachable, or remove the env var if you did not intend to target a custom API."
        : "The `project info` request could not reach the API successfully. Verify network reachability and retry.",
    );
  }

  return hints;
}
